<template>
  <base-modal
    :ref="modal.key"
    :show="open"
    :title="modal.title"
    @on-confirm="save"
    @on-cancel="hide"
    :okText="$t('Save')"
    :closeText="$t('Close')"
    maxWidth="70%"
  >
    <v-row v-if="fieldsValues.length == 0" justify="center" align="center">
      <v-col cols="1">
        <v-btn
          tile
          color="primary"
          prepend-icon="mdi-plus"
          outlined
          @click="addField()"
        >
          {{ $t("Add") }}
        </v-btn>
      </v-col>
    </v-row>
    <div v-for="item in fieldsValues" :key="item.uuid">
      <v-row>
        <v-col cols="1" class="text-left">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                tile
                large
                color="primary"
                icon
                @click="removeField(item)"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("Clear") }}</span>
          </v-tooltip>
        </v-col>
        <v-col cols="3" class="pl-0">
          <v-autocomplete
            v-model="item.valueType"
            :items="valueTypes"
            :label="$t('ValueType')"
            item-text="text"
            item-value="value"
          ></v-autocomplete>
        </v-col>
        <v-col cols="3" v-if="item.valueType == 'Dynamic'">
          <v-autocomplete
            v-model="item.fieldCode"
            :items="getFieldTitles(item)"
            item-text="DisplayText"
            item-value="Code"
            :label="$t('Field')"
          ></v-autocomplete>
        </v-col>
        <v-col cols="3" v-if="item.valueType == 'Dynamic'" class="pl-0">
          <v-autocomplete
            v-model="item.keyword"
            :items="keywords"
            :label="$t('Keyword')"
            item-text="text"
            item-value="value"
            @change="keywordChanged(item)"
          ></v-autocomplete>
        </v-col>
        <v-col cols="1" v-if="item.keyword == '%FOLDER%'">
          <v-text-field
            v-model="item.fieldValue"
            type="number"
            :label="$t('Index')"
          />
        </v-col>
        <v-col
          cols="6"
          v-if="item.valueType == 'Static'"
          class="pl-3 py-0 my-0"
        >
          <SearchCriteriaCondition
            :ref="`searchCriteriaConditionKey${item.uuid}`"
            :fieldList="getFieldTitles(item)"
            :showSearchOperatorSelect="false"
            :showAddButton="false"
            :conditionItem="item"
            :clearValue="false"
          />
        </v-col>
        <v-col cols="1" align-self="center" class="text-right">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                v-show="showAddButton"
                v-on="on"
                class="ml-5 mb-3"
                tile
                large
                color="primary"
                icon
                @click="addField()"
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("Add") }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </div>
  </base-modal>
</template>
<script>
import ContentTypeAutoImportConfigField from "@/ecm/models/contentType/autoImport/ContentTypeAutoImportConfigField";
import ConditionItem from "@/ecm/models/search/ConditionItem";
import FieldType from "@/ecm/variables/FieldType";
import SearchService from "@/services/SearchService";
import SearchCriteriaCondition from "@/components/search/SearchCriteriaCondition.vue";
import SearchConstValues from "@/ecm/variables/SearchConstValues";
import SearchUtils from "@/components/search/utilsSearch/UtilsSearch.js";

export default {
  name: "FieldsModal",
  components: {
    SearchCriteriaCondition,
  },
  props: {
    contentTypeId: {
      type: Number,
      required: true,
    },
    contentTypeFields: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      modal: {
        title: this.$t("Fields"),
        key: "fieldsModalKey",
      },
      open: false,
      fieldsValues: [],
      valueTypes: [
        {
          value: "Static",
          text: this.$t("Static"),
        },
        {
          value: "Dynamic",
          text: this.$t("Dynamic"),
        },
      ],
      keywords: [
        {
          value: "%FILENAME%",
          text: this.$t("FilenameWithExtension"),
        },
        {
          value: "%FILENAMENOEXT%",
          text: this.$t("Filename"),
        },
        {
          value: "%FILEPATH%",
          text: this.$t("Filepath"),
        },
        {
          value: "%FOLDER%",
          text: this.$t("FolderFromFilepath"),
        },
      ],
    };
  },
  computed: {
    showAddButton() {
      return this.fieldsValues.length < this.fieldList.length;
    },
  },
  asyncComputed: {
    fieldList: {
      get() {
        return SearchService.getAdvSearchFields(this.contentTypeId).then(
          (data) => data
        );
      },
      default: [],
    },
  },
  methods: {
    edit(fields) {
      this.fieldsValues = [];
      fields.forEach((item) => {
        var fieldType = this.fieldList.find(
          (x) => x.Code === item.defFieldCode
        ).Type;

        var conditionStr = `${item.defFieldCode}|s01|${item.value}|${FieldType[fieldType]}`;
        var conditionItem = new ConditionItem(conditionStr);
        if (item.value.startsWith("%")) {
          conditionItem.valueType = "Dynamic";
          conditionItem.keyword = item.value;
          if (item.value.startsWith("%FILEPATH")) {
            let lastChar = item.value.slice(-2, -1);
            if (lastChar != "H") {
              conditionItem.keyword = "%FOLDER%";
              conditionItem.fieldValue = lastChar;
            }
          }
        }
        this.setDisplayValue(conditionItem);
        this.fieldsValues.push(conditionItem);
      });
      this.show();
    },
    getFieldTitles(item) {
      if (this.fieldList || this.fieldList.length > 0) {
        var fields = this.fieldList.filter(
          (f) =>
            !this.fieldsValues.some(
              (fi) => fi.fieldCode == f.Code && fi.uuid != item.uuid
            )
        );
        return fields;
      } else {
        return [];
      }
    },
    setDisplayValue(conditionItem) {
      const context = this
      if (!conditionItem.fieldDisplayValue) {
        conditionItem.fieldDisplayValue = SearchUtils.GetDisplayValueFromFieldValue(
            conditionItem.fieldValue, context
        );
      } else {
        conditionItem.fieldDisplayValue = SearchUtils.GetDisplayValueFromFieldValue(
            conditionItem.fieldDisplayValue, context
        );
      }
    },
    async save() {
      var fields = this.fieldsValues.map((x) => {
        var field = new ContentTypeAutoImportConfigField();
        field.defFieldCode = x.fieldCode;
        if (x.keyword.startsWith("%")) {
          if (x.keyword == "%FOLDER%") {
            x.keyword = "%FILEPATH" + x.fieldValue + "%";
          }
          field.value = x.keyword;
        } else {
          field.value = x.fieldValue;
        }
        return field;
      });
      var uniqueFields = [
        ...new Set(
          this.contentTypeFields
            .filter(
              (f) => f.unique && fields.some((a) => a.defFieldCode == f.code)
            )
            .map((f) => f.name)
        ),
      ];
      if (uniqueFields.length > 0) {
        if (uniqueFields.length > 0) {
          var confirmMessage = this.$t(
            "UniqueFieldsAutomaticActionsWarning"
          ).replaceAll("{0}", uniqueFields.join());

          var res = await this.$confirm(confirmMessage, {
            title: this.$t("Confirm"),
            icon: "mdi-alert",
            color: "warning",
            buttonTrueText: this.$t("Save"),
            buttonFalseText: this.$t("Cancel"),
          });

          if (res != 1) {
            return;
          }
        }
      }
      this.$emit(
        "on-saved",
        fields.filter((x) => x.defFieldCode != "")
      );
      this.hide();
    },
    keywordChanged(item) {
      if (item.keyword == "") {
        item.fieldValue = "";
        item.fieldDisplayValue = "";
      }
    },
    removeField(item) {
      this.fieldsValues = this.fieldsValues.filter((f) => f.uuid != item.uuid);
    },
    addField() {
      this.fieldsValues.push(new ConditionItem());
    },
    show() {
      this.open = true;
    },
    hide() {
      this.open = false;
    },
  },
};
</script>
